/**
 * One row of a queue: a glyph (the stage pie, an Agent's medallion, a
 * connector mark), the key, the title over a state line, and on the right the
 * age and the verb the row opens with. Rows never commit anything: a row only
 * opens its record, and the sheet footer is where a verb acts.
 */
import type { CSSProperties, KeyboardEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { cx } from "./primitives.js";

export interface RowProps {
  glyph?: ReactNode;
  /** The record's key in mono (`COREX-2088`, `#4419`). */
  rowKey?: ReactNode;
  title: ReactNode;
  /** The stage word (or kind) before the state line, in its own hue. */
  tag?: { label: ReactNode; color?: string };
  state?: ReactNode;
  when?: ReactNode;
  /** The word on the row's hover button: Review, Answer, Watch, Open. */
  verb?: ReactNode;
  sel?: boolean;
  /** Opens the record in place (the sheet). */
  onOpen?: () => void;
  /** Or a link to the record's page. */
  to?: string;
  className?: string;
}

export function Row({ glyph, rowKey, title, tag, state, when, verb = "Open", sel, onOpen, to, className }: RowProps) {
  const body = (
    <>
      {glyph ?? <span aria-hidden />}
      <span className="key">{rowKey}</span>
      <span className="main">
        <span className="title">{title}</span>
        {(tag || state != null) && (
          <span className="state">
            {tag && (
              <>
                <span
                  className="stage"
                  style={{ "--stage": tag.color ?? "var(--text-tertiary)" } as CSSProperties}
                >
                  {tag.label}
                </span>
                <span className="sep">·</span>
              </>
            )}
            <span>{state}</span>
          </span>
        )}
      </span>
      <span className="right">
        <span className="when">{when}</span>
        <span className="go">
          <span className="verb">{verb}</span>
        </span>
      </span>
    </>
  );
  const cls = cx("row", sel && "sel", className);
  if (to) {
    return (
      <Link to={to} className={cls} aria-current={sel ? "true" : undefined}>
        {body}
      </Link>
    );
  }
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onOpen?.();
  };
  return (
    <div className={cls} role="button" tabIndex={0} aria-pressed={sel} onClick={onOpen} onKeyDown={onKeyDown}>
      {body}
    </div>
  );
}
